import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Chat as ChatIcon } from '@mui/icons-material';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { getAuthAccessToken } from '../../utils/demoPreviewStorage';
import { getCameraSocketUrl } from '../../utils/cameraSocketUrl';
import { getCameraIoOptions } from '../../utils/cameraIoOptions';

function memberLabel(r) {
  const c = r.Contact;
  const u = r.User;
  const n = `${c?.first_name || u?.first_name || ''} ${c?.last_name || u?.last_name || ''}`.trim();
  return n || u?.email || c?.email || 'Team member';
}

const TeamCameraStream = () => {
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const urlCacheRef = useRef({});
  const [reportees, setReportees] = useState([]);
  const [loading, setLoading] = useState(true);
  /** 4 = 2 columns, 9 = 3 columns (all reportees shown; wraps to new rows). */
  const [gridSize, setGridSize] = useState(4);
  const [imgUrls, setImgUrls] = useState({});
  const [socketReady, setSocketReady] = useState(false);

  const applyFrame = useCallback((userId, buffer) => {
    if (!buffer) return;
    const blob = new Blob([buffer], { type: 'image/jpeg' });
    const old = urlCacheRef.current[userId];
    if (old) URL.revokeObjectURL(old);
    const u = URL.createObjectURL(blob);
    urlCacheRef.current[userId] = u;
    setImgUrls((prev) => ({ ...prev, [userId]: u }));
  }, []);

  const loadContext = useCallback(async () => {
    try {
      const r = await api.get('/my-team/context', { skipErrorToast: true });
      const list = r.data?.data?.reportees || [];
      setReportees(list);
    } catch {
      toast.error('Could not load your team');
      setReportees([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContext();
    const id = setInterval(loadContext, 5000);
    return () => clearInterval(id);
  }, [loadContext]);

  useEffect(() => {
    const token = getAuthAccessToken();
    if (!token) return undefined;
    const socketUrl = getCameraSocketUrl();
    const s = io(socketUrl, getCameraIoOptions(token));
    socketRef.current = s;
    let connectErrToastShown = false;
    s.on('connect', () => {
      connectErrToastShown = false;
      setSocketReady(true);
    });
    s.on('disconnect', () => setSocketReady(false));
    s.on('connect_error', (err) => {
      console.error('[team camera] connect_error', err?.message || err);
      setSocketReady(false);
      if (!connectErrToastShown) {
        connectErrToastShown = true;
        toast.error('Could not connect to live camera server.');
      }
    });
    s.on('camera:frame', (userId, buf) => {
      applyFrame(userId, buf);
    });
    return () => {
      s.disconnect();
      socketRef.current = null;
      setSocketReady(false);
      Object.values(urlCacheRef.current).forEach((u) => URL.revokeObjectURL(u));
      urlCacheRef.current = {};
    };
  }, [applyFrame]);

  useEffect(() => {
    const s = socketRef.current;
    if (!s?.connected) return undefined;
    const targets = reportees.map((r) => r.User?.id).filter(Boolean);
    targets.forEach((uid) => s.emit('camera:watch', { targetUserId: uid }));
    return () => {
      targets.forEach((uid) => s.emit('camera:unwatch', { targetUserId: uid }));
    };
  }, [reportees, socketReady]);

  const columns = gridSize === 9 ? 3 : 2;
  const openChat = (userId) => {
    if (!userId) {
      toast.info('This team member has no user account yet.');
      return;
    }
    navigate(`/chat?dmUserId=${encodeURIComponent(userId)}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!reportees.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          My Team — Camera Stream
        </Typography>
        <Alert severity="info">
          You do not have any direct reports, or you are not linked as an employee. The My Team section only appears
          when people report to you (reporting manager or manager assignment).
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            My Team — Camera Stream
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Live thumbnails for direct reports who are streaming. Managers can watch without the global &quot;camera
            watch&quot; permission.
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={gridSize}
          onChange={(_, v) => v != null && setGridSize(v)}
        >
          <ToggleButton value={4}>2×2</ToggleButton>
          <ToggleButton value={9}>3×3</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {!socketReady && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Connecting to live server… If this persists, check API URL and Socket.IO path (same as My camera).
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: `repeat(${Math.min(columns, 2)}, 1fr)`,
            md: `repeat(${columns}, 1fr)`,
          },
        }}
      >
        {reportees.map((r) => {
          const uid = r.User?.id;
          const label = memberLabel(r);
          const streaming = Boolean(uid && r.streaming);
          const src = uid ? imgUrls[uid] : null;

          return (
            <Card key={r.id} variant="outlined">
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {label}
                </Typography>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    pt: '56.25%',
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    overflow: 'hidden',
                    mb: 1.5,
                  }}
                >
                  {streaming && src ? (
                    <Box
                      component="img"
                      src={src}
                      alt=""
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 1,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {!uid
                          ? 'No user login — streaming unavailable'
                          : streaming
                            ? 'Waiting for video…'
                            : 'This team member is not streaming'}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ChatIcon />}
                  onClick={() => openChat(uid)}
                  disabled={!uid}
                >
                  Chat
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </Box>

    </Box>
  );
};

export default TeamCameraStream;
